/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link } from 'react-router';

import { MTablePagination } from '@material-table/core';
import { Visibility } from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import { HUMaterialTable, HUMaterialTableProps } from '../../components/HUMaterialTable/HUMaterialTable';
import { Namespaces } from '../../i18n';
import { JiraCustomerSupportTicket } from '../../types/support/JiraSupport';
import { fetchAdminJiraTickets, fetchJiraConfig } from '../../utils/queryFns';
import { reactToCreateJiraIssue } from '../../utils/reactions';
import { useFormatDate } from '../../utils/useFormatDate';
import { TablePagination } from '../SupportPage/jira/components/TablePagination';
import { TicketCreationControl } from '../SupportPage/jira/components/TicketCreationControl';
import { ViewTicketModal } from '../SupportPage/jira/components/ViewTicketModal';
import { ModalProps, ModalTypes } from '../SupportPage/jira/types';
import { useAdminJiraTicketsPageStyles } from './styles';
import { FetchParams } from './types';

export const AdminJiraTicketsPage = memo(() => {
  const [modal, setModal] = useState<ModalProps | null>(null);
  const [fetchParams, setFetchParams] = useState<FetchParams>({
    requestStatus: 'open',
    take: 50,
    skip: 0,
  });
  const { t } = useTranslation(Namespaces.General);
  const classes = useAdminJiraTicketsPageStyles();

  const { formatDate } = useFormatDate();

  const {
    data: tickets,
    isLoading: isTicketsLoading,
    isError,
  } = useQuery(
    [
      fetchAdminJiraTickets.key,
      {
        requestStatus: fetchParams.requestStatus,
        take: fetchParams.take,
        skip: fetchParams.skip,
      },
    ],
    fetchAdminJiraTickets.fn,
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    },
  );

  const { data: jiraConfig, isLoading: isJiraConfigLoading } = useQuery(fetchJiraConfig.key, fetchJiraConfig.fn, {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const jiraTickets = useMemo(() => tickets?.payload ?? [], [tickets]);

  const isLoading = useMemo(() => isTicketsLoading || isJiraConfigLoading, [isTicketsLoading, isJiraConfigLoading]);

  const configExists = useMemo(() => jiraConfig?.payload.status ?? false, [jiraConfig]);

  const showIssue = (rowData: JiraCustomerSupportTicket | JiraCustomerSupportTicket[]) => {
    const ticket = Array.isArray(rowData) ? rowData[0] : rowData;
    setModal({ ticketId: ticket.issueKey, type: ModalTypes.VIEW });
  };

  const createIssue = () => setModal({ type: ModalTypes.CREATE });

  const handleSuccess = () => reactToCreateJiraIssue();

  const closeModal = () => setModal(null);

  const getLabelKey = (key: string, isLabel?: boolean) =>
    isLabel ? `jira_support.labels.${key}` : `jira_support.${key}`;

  const renderStatusChip = useCallback(({ status }: JiraCustomerSupportTicket) => <Chip label={status.name} />, []);

  const tableConfig = useMemo<HUMaterialTableProps<JiraCustomerSupportTicket>>(
    () => ({
      columns: [
        {
          title: t(getLabelKey('issueKey', true)),
          field: 'issueKey',
        },
        {
          title: t(getLabelKey('summary', true)),
          field: 'summary',
        },
        {
          title: t(getLabelKey('createdAt', true)),
          field: 'createdAt',
          render: rowData => formatDate(rowData.createdAt),
        },
        {
          title: t(getLabelKey('status', true)),
          field: 'status',
          render: rowData => renderStatusChip(rowData),
        },
      ],
      data: jiraTickets,
      title: t(getLabelKey('issues')),
      actions: [
        {
          icon: () => <Visibility />,
          tooltip: t(getLabelKey('viewIssue', true)),
          onClick: (_, rowData) => showIssue(rowData),
        },
      ],
      options: {
        actionsColumnIndex: -1,
        draggable: false,
        sorting: true,
        search: true,
      },
      components: {
        Pagination: props => (
          <>
            <TablePagination {...{ fetchParams, setFetchParams, isLoading }} />
            <MTablePagination {...props} />
          </>
        ),
      },
    }),
    [jiraTickets, t, formatDate, renderStatusChip, fetchParams, isLoading],
  );

  const modals = {
    [ModalTypes.CREATE]: <TicketCreationControl onClose={closeModal} onSuccess={handleSuccess} />,
    [ModalTypes.VIEW]: <ViewTicketModal ticketId={modal?.ticketId} onClose={closeModal} />,
  };

  return configExists ? (
    <>
      <Box className={classes.root}>
        <Box className={classes.container}>
          <Box className={classes.title}>{t(getLabelKey('title'))}</Box>
          <Button variant="contained" className={classes.newIssueButton} onClick={createIssue}>
            {t(getLabelKey('new_issue'))}
          </Button>
        </Box>
        {isLoading ? (
          <AlignCenter className={classes.loaderContainer}>
            <CircularProgress color="primary" size={40} />
          </AlignCenter>
        ) : isError ? (
          <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
        ) : (
          <HUMaterialTable {...tableConfig} />
        )}
      </Box>
      {modal && modals[modal.type]}
    </>
  ) : (
    <Alert severity="warning">
      <Trans
        i18nKey="general:jira_support.notConfiguredAdmin"
        components={{
          b: <b />,
          supportLink: <Link to="/admin/support-config" />,
        }}
      />
    </Alert>
  );
});
